import { guards } from '@shared/helpers/typeguards.helpers';
import { getSbefLabel } from '@shared/helpers/sbef.helpers';
import { isSbefCode, isSbefGroupCode } from '@shared/helpers/sbef.helpers';
import {
  SBEF_CUSTOM_GROUP_IDS,
  SbefCustomGroupId,
} from '@shared/construction/sbef-custom-groups.data';
import { SBEFCustomGroup } from '@shared/construction/sbef-custom-group.model';
import { SbefCode, SbefGroupCode } from '@shared/construction/sbef.model';
import { uniq, uniqBy } from 'lodash';
//

export const NO_SBEF_GROUP_KEY = 'no-sbef-group-found';
/** @deprecated Legacy catch-all bucket key — normalized to {@link SITE_OPERATIONS_SBEF_GROUP_KEY}. */
export const NO_SBEF_GROUP_LABEL = 'General';
export const SITE_OPERATIONS_SBEF_GROUP_KEY = SbefGroupCode.CommonWorks;
export const SITE_OPERATIONS_SBEF_GROUP_LABEL = 'Site operations';

/** Legacy bucket keys that fold into {@link SITE_OPERATIONS_SBEF_GROUP_KEY}. */
export const isLegacySiteOperationsSbefGroupKey = (key: string): boolean =>
  key === NO_SBEF_GROUP_KEY ||
  SBEF_CUSTOM_GROUP_IDS.includes(key as SbefCustomGroupId);

/**
 * Map persisted or resolved group keys to the canonical scope key used in lists and filters.
 */
export const normalizeSbefScopeGroupKey = (
  key: string,
): SbefGroupCode | string => {
  if (isLegacySiteOperationsSbefGroupKey(key)) {
    return SITE_OPERATIONS_SBEF_GROUP_KEY;
  }
  return key;
};

export enum SbefResolvedGroupKind {
  Standard = 'standard',
  Code = 'code',
  Custom = 'custom',
  None = 'none',
}

export type SbefResolvedGroup = { label: string } & (
  | { kind: SbefResolvedGroupKind.Custom; key: string; group: SBEFCustomGroup }
  | { kind: SbefResolvedGroupKind.Standard; key: SbefGroupCode }
  | { kind: SbefResolvedGroupKind.Code; key: SbefCode }
  | { kind: SbefResolvedGroupKind.None; key: typeof NO_SBEF_GROUP_KEY }
);

const NoGroup: SbefResolvedGroup = {
  kind: SbefResolvedGroupKind.None,
  key: NO_SBEF_GROUP_KEY,
  label: NO_SBEF_GROUP_LABEL,
};

export const getSbefGroupByCode = (
  codeOrGroupId: string | undefined,
  customGroups: SBEFCustomGroup[] = [],
): SbefResolvedGroup => {
  const matchedGroup = customGroups.find((g) => {
    const groupIdMatch = g.id === codeOrGroupId;
    const codeMatch =
      isSbefCode(codeOrGroupId) && g.isPartOfGroup(codeOrGroupId);
    return groupIdMatch || codeMatch;
  });

  if (matchedGroup)
    return {
      kind: SbefResolvedGroupKind.Custom,
      key: matchedGroup.id,
      label: matchedGroup.label,
      group: matchedGroup,
    };

  if (guards.is.undefined(codeOrGroupId) || !isSbefCode(codeOrGroupId))
    return NoGroup;

  // handle 100+ codes
  if (codeOrGroupId.length >= 3)
    return {
      kind: SbefResolvedGroupKind.Code,
      key: codeOrGroupId,
      label: getSbefLabel(codeOrGroupId),
    };

  // if code is part of a group, return it
  const first = codeOrGroupId.charAt(0);
  if (isSbefGroupCode(first))
    return {
      kind: SbefResolvedGroupKind.Standard,
      key: first,
      label: getSbefLabel(first),
    };

  return NoGroup;
};

/**
 * Label for an SBEF group row in the element list (group by code).
 */
export const getSbefGroupListLabel = (
  groupKey: string,
  customGroups: SBEFCustomGroup[] = [],
): string => {
  const normalizedKey = normalizeSbefScopeGroupKey(groupKey);
  if (normalizedKey === SITE_OPERATIONS_SBEF_GROUP_KEY) {
    return `${SITE_OPERATIONS_SBEF_GROUP_KEY}. ${SITE_OPERATIONS_SBEF_GROUP_LABEL}`;
  }
  return getSbefGroupByCode(normalizedKey, customGroups).label;
};

/**
 * Resolve the scope group key for an element SBEF code. General content, group 9, A5 custom
 * groups, and other 100-series codes all map to {@link SITE_OPERATIONS_SBEF_GROUP_KEY}.
 */
export const getSbefScopeGroupKeyForCode = (
  code: string | undefined,
  customGroups: SBEFCustomGroup[] = [],
): SbefGroupCode | string => {
  const resolved = getSbefGroupByCode(code, customGroups);
  if (resolved.kind === SbefResolvedGroupKind.Standard) return resolved.key;
  return SITE_OPERATIONS_SBEF_GROUP_KEY;
};

/**
 * Fold legacy SBEF group name keys (General, A5 custom) into group 9 when present.
 */
export const migrateLegacySbefGroupNames = (
  names?: Partial<Record<string, string>>,
): Partial<Record<string, string>> | undefined => {
  if (!names) return undefined;
  const legacyKeys = Object.keys(names).filter(
    isLegacySiteOperationsSbefGroupKey,
  );
  if (legacyKeys.length === 0) return undefined;

  const out = { ...names };
  let legacyName: string | undefined;
  for (const key of legacyKeys) {
    const name = out[key];
    if (!legacyName && name) legacyName = name;
    delete out[key];
  }
  if (legacyName && !out[SITE_OPERATIONS_SBEF_GROUP_KEY]) {
    out[SITE_OPERATIONS_SBEF_GROUP_KEY] = legacyName;
  }
  return out;
};

const defaultOrder: readonly SbefResolvedGroupKind[] = [
  SbefResolvedGroupKind.Standard,
  SbefResolvedGroupKind.Code,
  SbefResolvedGroupKind.Custom,
  SbefResolvedGroupKind.None,
];
export const orderSbefResolvedGroups = (
  groups: SbefResolvedGroup[],
  order: readonly SbefResolvedGroupKind[] = defaultOrder,
): SbefResolvedGroup[] => {
  const uniqueSorted = uniqBy(groups, 'key').sort((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
  );
  return uniq(order).flatMap((kind) =>
    uniqueSorted.filter((g) => g.kind === kind),
  );
};
